<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useAdaStore } from '../../stores/ada'

export type AdSlotName = 'dmofooter' | 'mobile' | 'outstream_div' | 'rectangle' | 'skyscraper' | 'superbanner' | 'teaser' | 'wallpaper' | 'tbsuperbanner' | 'tbmobile'

const props = withDefaults(defineProps<{
  slotName: AdSlotName,
  slotId?: number
}>(), {
  slotId: 1
})

const { blockAds, predictedMinHeights } = storeToRefs(useAdaStore())

const showAd = ref(true)
const rootEl = ref<HTMLElement | null>(null)
let minHeightObserver: ResizeObserver | null = null

const adSlotId = computed(() => props.slotId ? `${props.slotName}_${props.slotId}` : props.slotName)
const predictedMinHeight = computed(() => predictedMinHeights.value[adSlotId.value])

const adSlotClasses = computed(() => ({
  'bx-ada': true,
  [`bx-ada--${props.slotName}`]: true,
  [`bx-ada--${props.slotName}_${props.slotId}`]: props.slotName !== 'outstream_div'
}))

// ad reloads (time/interaction based) can render bigger or smaller creatives than the current min-height;
// we only ever raise it so the layout shifts at most once (when growing) and never snaps back down (when shrinking)
const observeMinHeightGrowth = () => {
  const adContainer = rootEl.value?.querySelector('.MoltenBundle')
  if (!adContainer) return
  minHeightObserver = new ResizeObserver(() => {
    // measured on the root element, not the ad container, since the root also carries spacing to neighboring content
    const newHeight = rootEl.value!.offsetHeight
    const currentMinHeight = parseFloat(rootEl.value!.style.minHeight) || parseFloat(getComputedStyle(rootEl.value!).minHeight) || 0
    if (newHeight > currentMinHeight) {
      // !important because the ad provider's own script sometimes overwrites the min-height it finds on OUR wrapper
      rootEl.value!.style.setProperty('min-height', `${newHeight}px`, 'important')
    }
  })
  minHeightObserver.observe(adContainer)
}

onMounted(() => {
  showAd.value = !blockAds.value
  const vh = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
  if (!blockAds.value) {
    const offsetTop = rootEl.value!.getBoundingClientRect().top + window.scrollY
    // remove min-heigth from native teaser ad slots that are not above the fold
    if (props.slotName === 'teaser' && offsetTop > vh) {
      rootEl.value!.classList.add('bx-ada--no-min-height')
    }
    // the ad provider's script overwrites our class-based min-height on mobile slots, so pin it inline right away
    if (props.slotName === 'mobile' && predictedMinHeight.value != null) {
      rootEl.value!.style.setProperty('min-height', `${predictedMinHeight.value}px`, 'important')
    }
    // only observe slots that actually have a predicted min-height, instead of excluding individual slot ids
    if (predictedMinHeight.value != null) {
      observeMinHeightGrowth()
    }
  }
})

onBeforeUnmount(() => {
  minHeightObserver?.disconnect()
})
</script>

<template>
  <div v-if="showAd"
       ref="rootEl"
       :class="adSlotClasses"
       :data-ad-slot-id="adSlotId">
    <div :id="adSlotId"
         class="MoltenBundle" />
    <slot name="ncbFallbackTeaser" />
  </div>
</template>
